/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './styles.sass';

type Height = 40 | 46 | 50 | 70;

type CommonProps = {
  children: React.ReactNode;
  isDisabled?: boolean;
  color?: 'white' | 'purple' | 'red' | 'green' | 'aqua' | 'black';
  className?: string;
  height?: Height;
  dataCy?: string;
};

type ButtonProps = {
  kind: 'button';
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
} & CommonProps;

type LinkProps = {
  kind: 'link';
  href: string;
  target?: string;
  rel?: string;
  download?: string;
} & CommonProps;

type Props = ButtonProps | LinkProps;

export const Button = (props: Props): JSX.Element => {
  const { isDisabled, className, color, children, height, dataCy } = props;
  const cx = classNames(
    styles.button,
    color !== undefined ? styles[color] : null,
    className !== undefined ? { [className]: className } : null,
    { [styles.disabled]: isDisabled }
  );
  const cssProp = css`
    height: ${height}px;
  `;

  switch (props.kind) {
    case 'link': {
      const { href, target, rel, download } = props;
      const linkProps = {
        className: cx,
        css: cssProp,
        target,
        rel,
        download,
        'data-cy': dataCy,
      };
      return href.startsWith('http') ? (
        <a {...linkProps} href={href}>
          {children}
        </a>
      ) : (
        <Link {...linkProps} to={href}>
          {children}
        </Link>
      );
    }
    case 'button':
    default: {
      const { type, onClick } = props;
      return (
        <button
          type={type || 'button'}
          className={cx}
          onClick={onClick}
          css={cssProp}
          data-cy={dataCy}
          disabled={isDisabled}
        >
          {children}
        </button>
      );
    }
  }
};

Button.defaultProps = {
  kind: 'button',
  height: 46,
};
